import { useState, useCallback } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';

/**
 * Custom hook for managing scenario CRUD operations
 * Handles: edit, delete, duplicate, transfer reader costs, bulk operations
 */
export function useScenarioManagement({
  selectedProposalId,
  proposalScenarios,
  setProposalScenarios,
  fetchProposalScenarios,
  // State setters from Calculator
  setScenarioInfo,
  setVolumeAssumptions,
  setIncumbentPricing,
  setProposedPricing,
  setTransactionDistribution,
  setProfitabilityResult,
  setSelectedProposalId,
  setEditMode,
  setEditingScenarioId,  // Track which scenario is being edited
  setReaderCostRecoveryApplied,  // NEW: Restore recovery state
  setOriginalRateBeforeRecovery  // NEW: Restore original rates
}) {
  const [scenarioToDelete, setScenarioToDelete] = useState(null);
  const [readerCostTransfer, setReaderCostTransfer] = useState(null);
  const [selectedScenarios, setSelectedScenarios] = useState(new Set());
  const [loading, setLoading] = useState(false);

  // ═══════════════════════════════════════════════════════════
  // SCENARIO LOADING & EDITING
  // ═══════════════════════════════════════════════════════════

  const handleEditScenario = useCallback(async (scenario) => {
    try {
      console.log('[useScenarioManagement] Loading scenario for edit:', scenario.id);
      
      // Set the editing scenario ID so we can update instead of create
      if (setEditingScenarioId) {
        setEditingScenarioId(scenario.id);
      }
      
      // Extract data from scenario (supports both top-level and nested 'inputs' structure)
      const scenarioInfoData = scenario.scenario_info || scenario.inputs?.scenario_info;
      const volumeData = scenario.volume_assumptions || scenario.inputs?.volume_assumptions;
      const incumbentData = scenario.incumbent_pricing || scenario.inputs?.incumbent_pricing;
      const proposedData = scenario.proposed_pricing || scenario.inputs?.proposed_pricing;
      const transactionData = scenario.transaction_distribution || scenario.inputs?.transaction_distribution;
      const profitabilityData = scenario.profitability_results || scenario.calculation_results;
      const readerRecoveryData = scenario.reader_cost_recovery;
      
      // Load scenario info with defaults
      if (scenarioInfoData) {
        const scenarioInfoWithDefaults = {
          ...scenarioInfoData,
          readerType: scenarioInfoData.readerType || 'N/A',
          readersQty: scenarioInfoData.readersQty !== undefined ? scenarioInfoData.readersQty : 0
        };
        setScenarioInfo(scenarioInfoWithDefaults);
      }
      
      if (volumeData) setVolumeAssumptions(volumeData);
      if (incumbentData) setIncumbentPricing(incumbentData);
      if (proposedData) setProposedPricing(proposedData);
      if (transactionData) setTransactionDistribution(transactionData);
      if (profitabilityData) {
        setProfitabilityResult(profitabilityData);
      } else {
        setProfitabilityResult(null);
      }
      
      // CRITICAL: Restore reader cost recovery state
      if (readerRecoveryData && readerRecoveryData.applied) {
        console.log('[useScenarioManagement] Restoring reader cost recovery state:', readerRecoveryData);
        if (setReaderCostRecoveryApplied) {
          setReaderCostRecoveryApplied(true);
        }
        if (setOriginalRateBeforeRecovery && readerRecoveryData.original_rates) {
          setOriginalRateBeforeRecovery(readerRecoveryData.original_rates);
        }
      } else {
        // No recovery applied, reset state
        if (setReaderCostRecoveryApplied) {
          setReaderCostRecoveryApplied(false);
        }
        if (setOriginalRateBeforeRecovery) {
          setOriginalRateBeforeRecovery(null);
        }
      }
      
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      const serviceName = scenario.service_name || scenario.scenario_info?.service || scenario.inputs?.scenario_info?.service || 'Scenario';
      const recoveryMsg = readerRecoveryData?.applied ? ' (Reader cost recovery already applied)' : '';
      toast.success(`📝 ${serviceName} loaded for editing${recoveryMsg}`);
    } catch (error) {
      console.error('[useScenarioManagement] Error loading scenario:', error);
      toast.error('Failed to load scenario for editing');
    }
  }, [setScenarioInfo, setVolumeAssumptions, setIncumbentPricing, setProposedPricing, setTransactionDistribution, setProfitabilityResult, setEditingScenarioId, setReaderCostRecoveryApplied, setOriginalRateBeforeRecovery]);

  const loadScenarioById = useCallback(async (scenarioId) => {
    try {
      console.log('[useScenarioManagement] Loading scenario by ID:', scenarioId);
      const response = await api.get(`/scenarios/${scenarioId}`);
      const scenario = response.data;
      
      // Set the proposal if the scenario belongs to one
      if (scenario.collection_id) {
        setSelectedProposalId(scenario.collection_id);
        setEditMode(true);
      }
      
      // Load the scenario data into the form
      await handleEditScenario(scenario);
    } catch (error) {
      console.error('[useScenarioManagement] Error loading scenario by ID:', error);
      toast.error('Failed to load calculation for editing');
    }
  }, [handleEditScenario, setSelectedProposalId, setEditMode]);

  // ═══════════════════════════════════════════════════════════
  // SCENARIO DELETION & READER COST TRANSFER
  // ═══════════════════════════════════════════════════════════

  const handleRemoveFromProposal = useCallback((scenarioId) => {
    const scenario = proposalScenarios.find(s => s.id === scenarioId);
    const scenarioName = scenario?.service_name || scenario?.scenario_info?.service || scenario?.name || 'this calculation';
    setScenarioToDelete({ id: scenarioId, name: scenarioName });
  }, [proposalScenarios]);
  
  const confirmRemoveScenario = useCallback(async () => {
    if (!scenarioToDelete) return;
    
    try {
      const scenario = proposalScenarios.find(s => s.id === scenarioToDelete.id);
      const hasReaderCosts = scenario?.net_fees?.reader_costs?.total_cost > 0;
      const readerCostAmount = scenario?.net_fees?.reader_costs?.total_cost || 0;
      
      if (hasReaderCosts) {
        const remainingServices = proposalScenarios.filter(s => s.id !== scenarioToDelete.id);
        
        if (remainingServices.length > 0) {
          // Show reader cost transfer modal
          setReaderCostTransfer({
            scenarioId: scenarioToDelete.id,
            scenarioName: scenarioToDelete.name,
            readerCosts: readerCostAmount,
            readerCostDetails: scenario.net_fees.reader_costs,
            remainingServices: remainingServices,
            scenario: scenario
          });
          setScenarioToDelete(null);
          return;
        }
      }
      
      // No reader costs or no remaining services - proceed with deletion
      await api.delete(`/scenarios/${scenarioToDelete.id}`);
      toast.success('Calculation removed from proposal');
      setScenarioToDelete(null);
      await fetchProposalScenarios(selectedProposalId);
    } catch (error) {
      console.error('Error removing scenario:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove calculation');
    }
  }, [scenarioToDelete, proposalScenarios, selectedProposalId, fetchProposalScenarios]);

  const handleTransferReaderCosts = useCallback(async (targetServiceId) => {
    if (!readerCostTransfer) return;
    
    try {
      setLoading(true);
      const targetScenario = readerCostTransfer.remainingServices.find(s => s.id === targetServiceId);
      
      if (!targetScenario) {
        toast.error('Target service not found');
        return;
      }
      
      const updatedReaderCosts = {
        quantity: readerCostTransfer.readerCostDetails.quantity || 0,
        cost_per_reader: readerCostTransfer.readerCostDetails.cost_per_reader || 0,
        total_cost: readerCostTransfer.readerCosts
      };
      
      await api.patch(`/scenarios/${targetServiceId}`, {
        reader_costs: updatedReaderCosts
      });
      
      await api.delete(`/scenarios/${readerCostTransfer.scenarioId}`);
      
      toast.success(`Reader costs ($${readerCostTransfer.readerCosts.toLocaleString()}) transferred to ${targetScenario.service_name || targetScenario.name}`);
      
      setReaderCostTransfer(null);
      await fetchProposalScenarios(selectedProposalId);
    } catch (error) {
      console.error('Error transferring reader costs:', error);
      toast.error(error.response?.data?.detail || 'Failed to transfer reader costs');
    } finally {
      setLoading(false);
    }
  }, [readerCostTransfer, selectedProposalId, fetchProposalScenarios]);

  const handleDeleteWithoutTransfer = useCallback(async () => {
    if (!readerCostTransfer) return;
    
    try {
      setLoading(true);
      await api.delete(`/scenarios/${readerCostTransfer.scenarioId}`);
      toast.success('Calculation removed (reader costs discarded)');
      setReaderCostTransfer(null);
      await fetchProposalScenarios(selectedProposalId);
    } catch (error) {
      console.error('Error deleting scenario:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove calculation');
    } finally {
      setLoading(false);
    }
  }, [readerCostTransfer, selectedProposalId, fetchProposalScenarios]);

  // ═══════════════════════════════════════════════════════════
  // BULK OPERATIONS
  // ═══════════════════════════════════════════════════════════

  const handleBulkDelete = useCallback(async () => {
    if (selectedScenarios.size === 0) {
      toast.error('Please select at least one calculation to delete');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedScenarios.size} calculation(s)? This cannot be undone.`
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      const selectedIds = Array.from(selectedScenarios);
      await Promise.all(selectedIds.map(id => api.delete(`/scenarios/${id}`)));
      
      toast.success(`Successfully deleted ${selectedIds.length} calculation(s)`);
      setSelectedScenarios(new Set());
      
      if (selectedProposalId) {
        await fetchProposalScenarios(selectedProposalId);
      }
    } catch (error) {
      console.error('Error deleting scenarios:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete calculations');
    } finally {
      setLoading(false);
    }
  }, [selectedScenarios, selectedProposalId, fetchProposalScenarios]);

  const handleSelectScenario = useCallback((scenarioId) => {
    setSelectedScenarios(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(scenarioId)) {
        newSelected.delete(scenarioId);
      } else {
        newSelected.add(scenarioId);
      }
      return newSelected;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedScenarios.size === proposalScenarios.length) {
      setSelectedScenarios(new Set());
    } else {
      setSelectedScenarios(new Set(proposalScenarios.map(s => s.id)));
    }
  }, [selectedScenarios.size, proposalScenarios]);

  // ═══════════════════════════════════════════════════════════
  // SCENARIO DUPLICATION
  // ═══════════════════════════════════════════════════════════

  const handleDuplicateScenario = useCallback(async (scenario) => {
    try {
      const response = await api.post(`/scenarios/${scenario.id}/duplicate`, {});
      toast.success(`✅ Calculation duplicated: ${response.data.scenario.name}`);
      
      if (selectedProposalId) {
        await fetchProposalScenarios(selectedProposalId);
      }
    } catch (error) {
      console.error('Error duplicating scenario:', error);
      toast.error(error.response?.data?.detail || 'Failed to duplicate calculation');
    }
  }, [selectedProposalId, fetchProposalScenarios]);

  return {
    // State
    scenarioToDelete,
    readerCostTransfer,
    selectedScenarios,
    loading,
    // State setters
    setScenarioToDelete,
    setReaderCostTransfer,
    setSelectedScenarios,
    // Handlers
    handleEditScenario,
    loadScenarioById,
    handleRemoveFromProposal,
    confirmRemoveScenario,
    handleTransferReaderCosts,
    handleDeleteWithoutTransfer,
    handleBulkDelete,
    handleSelectScenario,
    handleSelectAll,
    handleDuplicateScenario
  };
}
